<template>
  <v-dialog
    :model-value="dialog && mode === 'rich'"
    max-width="1100px"
    @update:model-value="onDialogChange"
  >
    <v-card>
      <v-card-title class="text-h5 custom-card-header">
        Whale Profile
        <v-btn
          icon
          variant="text"
          class="to-the-right"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-6">
        <whale-profile-view :whale="whale" />

        <v-row
          dense
          class="mt-4"
        >
          <v-col
            v-for="stat in lifeSummary"
            :key="stat.label"
            cols="3"
          >
            <div class="text-caption text-medium-emphasis">
              {{ stat.label }}
            </div>
            <div class="text-h6">
              {{ stat.value }}
            </div>
          </v-col>
        </v-row>

        <div class="mt-4 life-actions">
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-chart-bar"
            :disabled="sightings.length === 0"
            @click="openYearChart"
          >
            Sightings by Year
          </v-btn>
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-map-marker"
            :disabled="resightPoints.length === 0"
            @click="openResightMap"
          >
            Resight Map
          </v-btn>
          <v-btn
            v-if="isOrca"
            variant="tonal"
            size="small"
            prepend-icon="mdi-file-tree"
            :disabled="!whale.matriline"
            @click="openFamilyTree"
          >
            Family Tree
          </v-btn>
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-history"
            @click="openHistory"
          >
            History
          </v-btn>
          <div
            v-if="lineageBreadcrumb"
            class="lineage-breadcrumb text-body-2 text-medium-emphasis"
          >
            {{ lineageBreadcrumb }}
          </div>
        </div>

        <v-dialog
          v-model="showYearChart"
          max-width="640px"
          @update:model-value="onYearChartDialog"
        >
          <v-card>
            <v-card-title class="text-h6 custom-card-header">
              Sightings by Year
              <v-btn
                icon
                variant="text"
                class="to-the-right"
                @click="closeYearChart"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
              <div class="year-chart-wrap">
                <canvas ref="yearChartCanvas" />
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn @click="closeYearChart">
                <v-icon>mdi-close</v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          v-model="showResightMap"
          max-width="820px"
          @update:model-value="onResightMapDialog"
        >
          <v-card>
            <v-card-title class="text-h6 custom-card-header">
              Resight Map
              <v-btn
                icon
                variant="text"
                class="to-the-right"
                @click="closeResightMap"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
              <div
                ref="resightMapEl"
                class="resight-map-wrap"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn @click="closeResightMap">
                <v-icon>mdi-close</v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          v-model="showFamilyTree"
          max-width="760px"
        >
          <v-card>
            <v-card-title class="text-h6 custom-card-header">
              Family Tree — {{ whale.matriline }} matriline
              <v-btn
                icon
                variant="text"
                class="to-the-right"
                @click="closeFamilyTree"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
              <div
                v-if="lineageLoading"
                class="py-4 text-medium-emphasis"
              >
                Loading family tree…
              </div>
              <template v-else>
                <div
                  v-if="lineageTree.length === 0"
                  class="text-medium-emphasis"
                >
                  No catalogued maternal relationships in this matriline yet.
                </div>
                <template v-else>
                  <div class="lineage-tree">
                    <div
                      v-for="row in lineageTree"
                      :key="row.whale.whaleId"
                      class="lineage-row"
                      :style="{ paddingLeft: (row.depth * 26) + 'px' }"
                    >
                      <span
                        v-if="!row.isRoot"
                        class="lineage-edge"
                        :class="'edge-' + row.confidenceKey"
                      />
                      <span
                        class="lineage-node"
                        :class="{ 'lineage-node-current': row.whale.whaleId === whale.whaleId }"
                      >
                        {{ row.whale.whaleId }}<template v-if="row.whale.whaleName"> · {{ row.whale.whaleName }}</template>
                        <span
                          v-if="row.whale.birthYear"
                          class="lineage-year"
                        >({{ row.whale.birthYear }})</span>
                      </span>
                    </div>
                  </div>
                  <div class="lineage-legend text-caption text-medium-emphasis mt-4">
                    <span class="legend-item"><span class="lineage-edge edge-known" /> Known</span>
                    <span class="legend-item"><span class="lineage-edge edge-probable" /> Probable</span>
                    <span class="legend-item"><span class="lineage-edge edge-possible" /> Possible</span>
                  </div>
                </template>
              </template>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn @click="closeFamilyTree">
                <v-icon>mdi-close</v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          v-model="showHistory"
          max-width="780px"
          scrollable
        >
          <v-card>
            <v-card-title class="text-h6 custom-card-header">
              History &mdash; {{ whale.whaleId }}
              <v-btn
                icon
                variant="text"
                class="to-the-right"
                @click="closeHistory"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4">
              <audit-log-panel
                :entries="whaleHistory"
                :loading="whaleHistoryLoading"
                empty-text="No catalogue changes recorded for this whale yet. (Edits to this whale's record appear here; sighting changes are tracked in the Data Quality audit log.)"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn @click="closeHistory">
                <v-icon>mdi-close</v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-divider class="my-6" />

        <h4 class="text-h6">
          Sightings
        </h4>
        <v-data-table
          :headers="sightingHeaders"
          :items="sightings"
          :loading="loading"
          density="compact"
          class="mt-2"
        />

        <v-divider class="my-6" />

        <h4 class="text-h6">
          Photo Gallery
        </h4>
        <v-progress-linear
          indeterminate
          :active="loading"
          color="primary"
        />
        <v-row class="mt-2">
          <v-col
            v-for="image in images"
            :key="image.id"
            cols="3"
          >
            <v-card variant="outlined">
              <v-img
                :src="thumbnailUrl(image)"
                aspect-ratio="1"
                class="bg-grey-lighten-2"
                cover
              />
              <v-card-subtitle class="py-2">
                {{ image.name }}
              </v-card-subtitle>
              <v-card-actions v-if="canEdit">
                <v-btn
                  v-if="whale.featurePhotoId === image.id"
                  size="small"
                  variant="text"
                  color="primary"
                  disabled
                >
                  Profile Photo
                </v-btn>
                <v-btn
                  v-else
                  size="small"
                  variant="text"
                  @click="setFeaturePhoto(image.id)"
                >
                  Set as Profile
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col
            v-if="!loading && images.length === 0"
            cols="12"
          >
            <span class="text-caption">No photos associated with this whale.</span>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="canEdit"
          color="primary"
          @click="enableEditing"
        >
          <v-icon>mdi-pencil</v-icon>
          Enable Editing
        </v-btn>
        <v-spacer />
        <v-btn @click="close">
          <v-icon>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WhaleProfileView from "@/components/whale/WhaleProfileView.vue";
import AuditLogPanel from "@/components/audit/AuditLogPanel.vue";
import Chart from "chart.js/auto";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import orcaTaxonomy from "@/helpers/orcaTaxonomy";

export default {
  components: {
    WhaleProfileView,
    AuditLogPanel,
  },
  data() {
    return {
      sightingHeaders: [
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Date", sortable: true, key: "sightingDate" },
        { title: "Species", sortable: true, key: "species" },
        { title: "Location", sortable: true, key: "locationDescription" },
        { title: "Behaviours", sortable: false, key: "bhv" },
      ],
      showYearChart: false,
      showResightMap: false,
      showFamilyTree: false,
      showHistory: false,
    };
  },
  computed: {
    ...mapGetters({
      whale: "whale/view/whale",
      mode: "whale/view/mode",
      dialog: "whale/view/dialog",
      sightings: "whale/view/sightings",
      images: "whale/view/images",
      loading: "whale/view/loading",
      whaleHistory: "audit/whaleEntries",
      whaleHistoryLoading: "audit/whaleLoading",
      hasScope: "auth/hasScope",
      lineage: "whale/view/lineage",
      lineageLoading: "whale/view/lineageLoading",
    }),
    canEdit() {
      return this.hasScope("whales:write");
    },
    lifeSummary() {
      const sightings = this.sightings || [];
      const years = sightings
          .map((sighting) => sighting.sightingYear)
          .filter((year) => year != null);
      const distinctYears = new Set(years);
      return [
        { label: "First Seen", value: years.length ? Math.min(...years) : "—" },
        { label: "Last Seen", value: years.length ? Math.max(...years) : "—" },
        { label: "Total Sightings", value: sightings.length },
        { label: "Years Observed", value: distinctYears.size },
      ];
    },
    yearSeries() {
      const counts = {};
      (this.sightings || []).forEach((sighting) => {
        const year = sighting.sightingYear;
        if (year == null) return;
        counts[year] = (counts[year] || 0) + 1;
      });
      const years = Object.keys(counts)
          .map(Number)
          .sort((a, b) => a - b);
      return {
        labels: years,
        data: years.map((year) => counts[year]),
      };
    },
    resightPoints() {
      const points = [];
      (this.sightings || []).forEach((sighting) => {
        const lat = parseFloat(sighting.lat);
        const lon = parseFloat(sighting.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return;
        const when = sighting.sightingDate
          ? String(sighting.sightingDate).slice(0, 10)
          : sighting.sightingYear || "";
        const label = [when, sighting.locationDescription]
          .filter(Boolean)
          .join(" — ");
        points.push({ lat, lon, label });
      });
      return points;
    },
    lineageBreadcrumb() {
      const whale = this.whale || {};
      if (!orcaTaxonomy.isOrca(whale.species)) return "";
      const parts = [];
      const ecotype = orcaTaxonomy.forcedEcotype(whale.species) || whale.ecotype;
      if (ecotype) parts.push(ecotype);
      if (whale.clan) parts.push(`${whale.clan} clan`);
      if (whale.pod) parts.push(`${whale.pod} pod`);
      if (whale.matriline) parts.push(`${whale.matriline} matriline`);
      return parts.join(" › ");
    },
    isOrca() {
      return orcaTaxonomy.isOrca(this.whale ? this.whale.species : null);
    },
    lineageTree() {
      const members = this.lineage || [];
      if (members.length === 0) return [];
      const byId = {};
      members.forEach((member) => {
        byId[member.whaleId] = member;
      });
      const childrenOf = {};
      const roots = [];
      members.forEach((member) => {
        if (member.mother && byId[member.mother]) {
          (childrenOf[member.mother] = childrenOf[member.mother] || []).push(member);
        } else {
          roots.push(member);
        }
      });
      const byBirth = (a, b) => (a.birthYear || 0) - (b.birthYear || 0);
      roots.sort(byBirth);
      const rows = [];
      const visited = new Set();
      const validKeys = ["known", "probable", "possible"];
      const walk = (member, depth, isRoot) => {
        if (visited.has(member.whaleId)) return;
        visited.add(member.whaleId);
        const confidence = (member.maternalConfidence || "").toLowerCase();
        rows.push({
          whale: member,
          depth,
          isRoot,
          confidenceKey: validKeys.includes(confidence) ? confidence : "known",
        });
        const kids = (childrenOf[member.whaleId] || []).slice().sort(byBirth);
        kids.forEach((kid) => walk(kid, depth + 1, false));
      };
      roots.forEach((root) => walk(root, 0, true));
      members.forEach((member) => {
        if (!visited.has(member.whaleId)) walk(member, 0, true);
      });
      return rows;
    },
  },

  beforeUnmount() {
    this.destroyYearChart();
    this.destroyResightMap();
  },

  methods: {
    ...mapActions({
      close: "whale/view/close",
      setFeaturePhoto: "whale/view/setFeaturePhoto",
      openEdit: "whale/details/open",
      loadLineage: "whale/view/loadLineage",
      loadWhaleHistory: "audit/loadWhaleHistory",
    }),
    thumbnailUrl(image) {
      const url = image && image.thumbnailUrl;
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    onDialogChange(value) {
      if (!value) {
        this.close();
      }
    },
    enableEditing() {
      const whale = this.whale;
      this.close();
      this.openEdit({ whale });
    },
    openHistory() {
      if (!this.whale || !this.whale.whaleId) return;
      this.showHistory = true;
      this.loadWhaleHistory(this.whale.whaleId);
    },
    closeHistory() {
      this.showHistory = false;
    },
    openYearChart() {
      this.showYearChart = true;
      this.$nextTick(() => this.renderYearChart());
    },
    closeYearChart() {
      this.showYearChart = false;
    },
    onYearChartDialog(value) {
      if (!value) {
        this.destroyYearChart();
      }
    },
    renderYearChart() {
      const canvas = this.$refs.yearChartCanvas;
      if (!canvas) return;
      this.destroyYearChart();
      const series = this.yearSeries;
      this.yearChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels: series.labels,
          datasets: [
            {
              label: "Sightings",
              data: series.data,
              backgroundColor: "#364563",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { autoSkip: false } },
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },
    destroyYearChart() {
      if (this.yearChart) {
        this.yearChart.destroy();
        this.yearChart = null;
      }
    },
    openResightMap() {
      this.showResightMap = true;
      this.$nextTick(() => this.createResightMap());
    },
    closeResightMap() {
      this.showResightMap = false;
    },
    onResightMapDialog(value) {
      if (!value) {
        this.destroyResightMap();
      }
    },
    createResightMap() {
      const el = this.$refs.resightMapEl;
      if (!el || this.resightMap) return;
      const points = this.resightPoints;
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
      this.resightMap = new mapboxgl.Map({
        container: el,
        style: "mapbox://styles/mapbox/outdoors-v12",
        center: [-128.9110, 54.6057],
        zoom: 4.15,
      });
      this.resightMap.addControl(new mapboxgl.NavigationControl());
      this.resightMap.on("load", () => {
        const bounds = new mapboxgl.LngLatBounds();
        points.forEach((point) => {
          const marker = new mapboxgl.Marker().setLngLat([point.lon, point.lat]);
          if (point.label) {
            marker.setPopup(new mapboxgl.Popup({ offset: 16 }).setText(point.label));
          }
          marker.addTo(this.resightMap);
          bounds.extend([point.lon, point.lat]);
        });
        if (!bounds.isEmpty()) {
          this.resightMap.fitBounds(bounds, { padding: 48, maxZoom: 9 });
        }
        this.resightMap.resize();
      });
      window.setTimeout(() => {
        if (this.resightMap) this.resightMap.resize();
      }, 300);
    },
    destroyResightMap() {
      if (this.resightMap) {
        this.resightMap.remove();
        this.resightMap = null;
      }
    },
    openFamilyTree() {
      this.showFamilyTree = true;
      if (this.whale && this.whale.matriline) {
        this.loadLineage(this.whale.matriline);
      }
    },
    closeFamilyTree() {
      this.showFamilyTree = false;
    },
  },
};
</script>

<style scoped>
.to-the-right {
  float: right;
}

.life-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.lineage-breadcrumb {
  margin-left: auto;
}

.year-chart-wrap {
  position: relative;
  height: 320px;
}

.resight-map-wrap {
  width: 100%;
  height: 60vh;
  min-height: 360px;
}

.lineage-tree {
  font-size: 0.95rem;
  line-height: 1.9;
}

.lineage-row {
  display: flex;
  align-items: center;
}

.lineage-edge {
  display: inline-block;
  width: 18px;
  margin-right: 8px;
  border-top: 2px solid #6B7280;
}

.edge-known {
  border-top-style: solid;
}

.edge-probable {
  border-top-style: dashed;
}

.edge-possible {
  border-top-style: dotted;
}

.lineage-node-current {
  font-weight: 700;
  color: #364563;
}

.lineage-year {
  color: #6B7280;
  margin-left: 4px;
}

.lineage-legend {
  display: flex;
  gap: 16px;
}

.lineage-legend .legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.lineage-legend .lineage-edge {
  width: 24px;
  margin-right: 0;
}
</style>
